// ./src/components/generic/MediaSelector/mediaSelectorConfig.ts
//
// The MediaSelector is opened with an *intent* — what the caller is choosing a
// media item for. The intent decides the media kind (image/video/audio), the
// storage `purpose` tag, the crop aspect for the editor, and whether a plain URL
// result is acceptable (some callers can only store an uploaded_file_id).
//
// Mirrors ms_storage's FILE_PURPOSES / MEDIA_KINDS vocabulary (see the storage
// boundary) so uploads land tagged consistently.

import { isStockSourceConfigured } from "./stockMedia";

export type MediaKind = "image" | "video" | "audio";

/** Crop aspect passed to ImageCropEditor (image intents only). */
export type MediaCropAspect = "free" | "square" | "4:3" | "3:4" | "16:9";

/** What the caller is picking a media item for. */
export type MediaIntent =
  | "cover-image" // event website cover image
  | "banner-image" // a WIDE strip across the top of a page (the organizer page's header) — displayed cropped, so 16:9
  | "cover-video" // event website cover video
  | "background-image"
  | "background-video"
  | "content-image" // an image shown in website content / a gallery
  | "content-video" // a video shown in website content (a video block)
  | "avatar" // user / guest avatar
  | "logo"
  | "audio" // music (the audio block's main track)
  | "background-audio"; // a sound under the music — rain, the ocean, a crowd (the library opens on Sounds)

export interface MediaIntentConfig {
  /** Media kind this intent produces. */
  kind: MediaKind;
  /** Storage `purpose` tag applied to uploads made through this intent. */
  purpose: string;
  /** `accept` attribute for the file input / drop zone. */
  accept: string;
  /** Default crop aspect for the image editor (ignored for video/audio). */
  cropAspect: MediaCropAspect;
  /**
   * Whether an external URL (paste-a-link) is a valid result for this intent.
   * Callers that can only persist an uploaded_file_id set this off. Also gates
   * the External URL tab's visibility.
   */
  allowUrl: boolean;
}

// #79 (owner, 2026-10-02) — a cover or background image starts in FREE form. A fixed 16:9 must COVER its frame, so a
// letter-size poster was cut to a strip before the host had touched anything; every place that shows a cover fits the
// whole picture in (`MediaHero`, `MediaImageField` — contain), so nothing needs it to be 16:9. The host can still pick
// 16:9 (or Letter / A4) in the editor.
export const MEDIA_INTENTS: Record<MediaIntent, MediaIntentConfig> = {
  "cover-image": { kind: "image", purpose: "cover", accept: "image/*", cropAspect: "free", allowUrl: true },
  "cover-video": { kind: "video", purpose: "cover_video", accept: "video/*", cropAspect: "16:9", allowUrl: true },
  // ⚠ NOT free, unlike the cover: the organizer page draws its header as a fixed-height strip cut to fit (`objectFit:
  //   cover`), so a poster there would show a slice of its middle. Shaping it to the strip at upload is the honest crop.
  "banner-image": { kind: "image", purpose: "cover", accept: "image/*", cropAspect: "16:9", allowUrl: true },
  "background-image": { kind: "image", purpose: "background", accept: "image/*", cropAspect: "free", allowUrl: true },
  "background-video": { kind: "video", purpose: "background", accept: "video/*", cropAspect: "16:9", allowUrl: true },
  "content-image": { kind: "image", purpose: "gallery", accept: "image/*", cropAspect: "free", allowUrl: true },
  "content-video": { kind: "video", purpose: "gallery", accept: "video/*", cropAspect: "free", allowUrl: true },
  avatar: { kind: "image", purpose: "avatar", accept: "image/*", cropAspect: "square", allowUrl: false },
  logo: { kind: "image", purpose: "logo", accept: "image/*", cropAspect: "free", allowUrl: true },
  audio: { kind: "audio", purpose: "general", accept: "audio/*", cropAspect: "free", allowUrl: true },
  "background-audio": { kind: "audio", purpose: "general", accept: "audio/*", cropAspect: "free", allowUrl: true },
};

// ============================================
// Tabs
// ============================================

export type MediaSelectorTab = "library" | "upload" | "gallery" | "unsplash" | "giphy" | "pexels" | "audio-library" | "url";

/**
 * Which tabs are offered for an intent, in display order. Stock sources
 * (Unsplash / GIPHY photos+gifs, Pexels photos/videos) yield a URL result, so
 * they only appear when a URL result is allowed AND the source's proxy is
 * configured. Unsplash + GIPHY are image-only; Pexels also covers video.
 * The plain URL tab always comes last.
 *
 * ⓘ The free music & sounds library (`audio-library`) is searched through OUR API for one EVENT (its white label holds
 * the keys), so it is offered only when the caller passes the event (`hasEventContext`).
 * ⓘ The brand's ready-made `gallery` (its STANDARD files in ms_storage — PartyLabz's cover pictures) yields a stored
 * file, so it is offered whatever the URL rule — but only when the brand HAS one for this kind and purpose
 * (`hasGallery`); EventsMagic has none, and an empty tab would be a dead end.
 */
export const getMediaTabs = (config: MediaIntentConfig, allowUrl: boolean, hasEventContext: boolean, hasGallery: boolean): MediaSelectorTab[] => {
  const tabs: MediaSelectorTab[] = ["library", "upload"];
  const canUrl = config.allowUrl && allowUrl;

  if (hasGallery) {
    tabs.push("gallery");
  }

  if (canUrl) {
    if (config.kind === "image") {
      if (isStockSourceConfigured("unsplash")) tabs.push("unsplash");
      if (isStockSourceConfigured("giphy")) tabs.push("giphy");
      if (isStockSourceConfigured("pexels")) tabs.push("pexels");
    } else if (config.kind === "video") {
      if (isStockSourceConfigured("pexels")) tabs.push("pexels");
    } else if (config.kind === "audio" && hasEventContext) {
      tabs.push("audio-library");
    }

    tabs.push("url");
  }

  return tabs;
};

// ============================================
// Result / value shapes
// ============================================

/** The current value the selector should preselect when it opens. */
export interface MediaSelectorValue {
  /** A previously chosen stored file. */
  uploadedFileId?: string | null;
  /** A previously entered external URL (or the stored file's URL). */
  url?: string | null;
}

/** What the selector hands back on confirm. */
export interface MediaSelectorResult {
  /** Set when the pick is a stored file (library pick or fresh upload). */
  uploadedFileId?: string | null;
  /** Always the resolvable URL — a stored file's fileUrl, or the external URL. */
  url: string;
  /** The media kind, echoing the intent. */
  kind: MediaKind;
  /** Where the pick came from. */
  source: MediaSelectorTab;
  /**
   * A library pick's credit — ⚠ REQUIRED by the CC BY licences most library tracks carry, so the caller stores it and
   * shows it with the track: the line, the track's page, and the licence's page (CC BY wants a link to the licence).
   * `title` is the track's name. Absent for the user's own files and pasted links.
   */
  credit?: { title: string; text: string; url: string | null; licenseUrl?: string | null } | null;
}

// ============================================
// Kind <-> mime helpers (client-side, mirrors storage mimeToKind)
// ============================================

const DOC_HINTS = ["pdf", "msword", "officedocument", "ms-excel", "ms-powerpoint", "text/", "rtf", "csv"];

export const mimeToMediaKind = (mimeType?: string | null): MediaKind | "other" => {
  const mime = (mimeType ?? "").toLowerCase();

  if (mime.startsWith("image/")) {
    return "image";
  }

  if (mime.startsWith("video/")) {
    return "video";
  }

  if (mime.startsWith("audio/")) {
    return "audio";
  }

  if (DOC_HINTS.some((hint) => mime.includes(hint))) {
    return "other";
  }

  return "other";
};

/** A raster image the browser can decode and is worth offering the crop editor for. */
export const isEditableImage = (file: File): boolean => {
  const isRaster = /^image\//i.test(file.type) && !/svg/i.test(file.type);
  const looksRaster = /\.(jpe?g|png|webp|avif|bmp|tiff?)$/i.test(file.name || "");

  return isRaster || looksRaster;
};
